<template>
	<div class="card !shadow-none !border-0">
		<!-- Loading State -->
		<div v-if="isLoading" class="flex items-center justify-center py-10">
			<i class="tabler:loader-2 w-8 h-8 animate-spin text-primary"></i>
			<span class="ml-2">Loading quiz...</span>
		</div>
		<!-- Error State -->
		<div v-else-if="fetchError" class="p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-circle text-red-600 text-lg mr-2"></i>
				<span class="text-red-800">{{ fetchError }}</span>
			</div>
		</div>
		<div v-else-if="!questions.length" class="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-r-lg">
			<div class="flex items-center">
				<i class="tabler:alert-triangle text-yellow-700 text-lg mr-2"></i>
				<span class="text-yellow-900">No PostQuiz questions found.</span>
			</div>
		</div>
		<!-- Quiz Content -->
		<template v-else>
		<QuizStepper orientation="vertical">
			<StepperPanel v-for="( question, index) in questions" :key="index" :active="currentQuestionIndex === index"
			:header="labels.questionLabel + (index + 1)"
			>
			<template #content="{ prevCallback, nextCallback }">
					<div class="flex flex-col">
						<InplaceEdit
							class="!items-start"
							:inputProp="{ content: question, inputType: 'quiz', propName: 'content' }"
							method="PUT"
							:href="question._links?.self.href"
							blockTitle="Edit Question"
							:showAssistant="true"
							>
							<span class="font-semibold">{{ question.question }}</span>
						</InplaceEdit>

						<div class="py-5 flex-auto flex flex-col justify-center items-start font-semibold">
							<div v-for="( answer, aIndex) in question.answers" :key="aIndex"
								class="p-4 w-full bg-gray-100">
								<div class="text-md flex">
									<QuizCheck :value="aIndex" v-model="selectedAnswers[index]"
										:inputId="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										:name="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length" />
									<label :for="'answer-' + index + 1 + '-' + aIndex + answer.answer?.length"
										class="ml-2 font-normal">
										{{ answer.answer }}
									</label>
								</div>
								<div v-if=" selectedAnswers[index].includes( aIndex ) ">
									<div class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-success/40 bg-success-100/10 text-dark"
										v-if=" answer.isCorrect ">
										<i
											class="tabler:circle-check !text-[35px] text-success max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>
										<span class="text-sm">{{ answer.explanation }} </span>
									</div>
									<div v-else
										class="flex items-start gap-2 px-3 font-normal py-3 mt-3 border rounded-md border-danger/40 bg-danger-100/10 text-dark">
										<i
											class="tabler:circle-x !text-[35px] text-danger max-w-[40px] !w-[40px] max-h-[40px] !h-[40px] min-w-[40px]"></i>

										<span class="text-sm">{{ answer.explanation }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="flex pt-4 justify-between">
						<EwButton v-if=" index " :label="'Back'" type="secondary" :icon="'tabler:arrow-narrow-left'"
							@click="prevCallback" />
						<EwButton v-if=" index < questions.length - 1 " type="success" :label="'Next'"
							:iconAppend="'tabler:arrow-narrow-right'" @click="nextCallback" />
						<EwButton v-if=" questions.length - 1 === index " type="success" :label="'Finish'"
							:iconAppend="'tabler:check'" @click="handleClose" />
					</div>
				</template>
			</StepperPanel>
		</QuizStepper>
		</template>
	</div>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { messages } from '@/assets/language';
import type { LanguageCodes, QuizQuestionsItem } from '@/types'
import InplaceEdit from './InplaceEdit/InplaceEdit.vue';
import { useAppStore } from '@/stores/app-store'


const { currentLng } = useAppStore()
const labels = computed( () => {
	const key = currentLng.value as keyof typeof messages
	return messages[key] ?? {}
});

const props = defineProps<{
  selectedLanguage?: LanguageCodes;
  overrideURL?: string; // URL with parameters to fetch quiz data
  jsonData: {
    postQuiz: { questions: QuizQuestionsItem[] };
	_links: { postQuiz: { href: string }; postQuizEdit: { href: string } };
  };
}>();

// Loading and error states for fetch
const isLoading = ref(false);
const fetchError = ref<string | null>(null);
const fetchedData = ref<{ postQuiz: { questions: QuizQuestionsItem[] } } | null>(null);

const isObjectRecord = (value: unknown): value is Record<string, any> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const normalizeAnswer = (answer: any) => ({
  ...answer,
  answer: String(answer?.answer ?? answer?.title ?? answer?.content ?? answer?.text ?? answer?.label ?? answer?.answerLabel ?? ''),
  answerLabel: String(answer?.answerLabel ?? answer?.label ?? answer?.answer ?? ''),
  explanation: String(answer?.explanation ?? answer?.answerFeedback ?? answer?.feedback ?? ''),
  isCorrect: answer?.isCorrect === true || answer?.isCorrect === 'true' || answer?.isCorrect === 'y' || answer?.isCorrectProvided === 'y',
});

const normalizeQuestion = (question: any): QuizQuestionsItem => ({
  ...question,
  id: String(question?.id ?? question?.identifier ?? ''),
  question: String(question?.question ?? question?.title ?? question?.content ?? question?.text ?? question?.label ?? ''),
  answers: Array.isArray(question?.answers) ? question.answers.map(normalizeAnswer) : [],
  _links: {
    ...question?._links,
    self: {
      href: question?._links?.self?.href ?? question?.href ?? props.overrideURL ?? '',
    },
  },
});

const resolvePostQuizQuestions = (source: unknown): QuizQuestionsItem[] => {
  if (Array.isArray(source)) {
    const directQuestions = source.filter((item) => isObjectRecord(item) && (Array.isArray(item.answers) || item.question !== undefined));
    if (directQuestions.length) {
      return directQuestions.map(normalizeQuestion);
    }

    for (const item of source) {
      const resolved = resolvePostQuizQuestions(item);
      if (resolved.length) {
        return resolved;
      }
    }
    return [];
  }

  if (!isObjectRecord(source)) {
    return [];
  }

  if (Array.isArray(source.questions)) {
    return source.questions.map(normalizeQuestion);
  }

  const nestedKeys = ['postQuiz', 'other', 'data', 'content', 'item', 'wiItem', 'items', 'children'];
  for (const key of nestedKeys) {
    if (source[key] !== undefined) {
      const resolved = resolvePostQuizQuestions(source[key]);
      if (resolved.length) {
        return resolved;
      }
    }
  }

  return [];
};

const normalizePostQuizData = (data: any): { postQuiz: { questions: QuizQuestionsItem[] } } => ({
  ...data,
  postQuiz: {
    ...(Array.isArray(data?.postQuiz) ? {} : data?.postQuiz),
    questions: resolvePostQuizQuestions(data),
  },
});

const parsePostQuizResponse = (responseText: string) => {
  const trimmedText = responseText.trim().replace(/^\uFEFF/, '');
  const parsed = JSON.parse(trimmedText);
  return typeof parsed === 'string' ? JSON.parse(parsed.trim().replace(/^\uFEFF/, '')) : parsed;
};

// Use fetched data if available, otherwise fall back to jsonData
const questions = computed(() => {
  if (fetchedData.value?.postQuiz?.questions) {
    return fetchedData.value.postQuiz.questions;
  }
  return resolvePostQuizQuestions(props.jsonData?.postQuiz);
});

// Fetch quiz data from overrideURL
const fetchQuizData = async () => {
  if (!props.overrideURL) {
    console.log('[PostQuiz] No overrideURL provided, using jsonData');
    return;
  }
  
  isLoading.value = true;
  fetchError.value = null;
  
  try {
    console.log('[PostQuiz] Fetching quiz from:', props.overrideURL);
    const response = await fetch(props.overrideURL, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    });
    
    if (!response.ok) {
      console.log('[PostQuiz] HTTP error, using jsonData:', response.status);
      fetchError.value = `Unable to load PostQuiz (${response.status}): ${props.overrideURL}`;
      return;
    }
    
    const responseText = await response.text();
    const data = parsePostQuizResponse(responseText);
    console.log('[PostQuiz] Fetched data:', data);
    
    const normalizedData = normalizePostQuizData(data);
    if (normalizedData.postQuiz.questions.length) {
      fetchedData.value = normalizedData;
    } else {
      console.log('[PostQuiz] Invalid response structure, using jsonData');
    }
  } catch (error) {
    console.log('[PostQuiz] Fetch error, using jsonData:', error);
    fetchError.value = `Unable to load PostQuiz: ${error instanceof Error ? error.message : String(error)} (${props.overrideURL})`;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.overrideURL,
  () => {
    if (props.overrideURL) {
      fetchQuizData();
    }
  },
  { immediate: true }
);

const selectedAnswers = ref<Array<Array<number>>>([]);

// Initialize and update selectedAnswers when questions change
watch(questions, (newQuestions) => {
  selectedAnswers.value = newQuestions.map(() => []);
}, { immediate: true });
const currentQuestionIndex = ref( 0 );
const emits = defineEmits( ['close'] );

const handleClose = () => {
	emits( 'close' );
};
</script>

<style scoped>
/* .p-stepper {
	flex-basis: 50rem;
} */
</style>
